
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Project } from '../types';
import OptimizedImage from './OptimizedImage';

interface HeroSliderProps {
    projects: Project[];
    fallback?: React.ReactNode;
}

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
);

const HeroSlider: React.FC<HeroSliderProps> = ({ projects, fallback }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const goToPrev = () => {
        const isFirstSlide = currentIndex === 0;
        const newIndex = isFirstSlide ? projects.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
    };

    const goToNext = () => {
        const isLastSlide = currentIndex === projects.length - 1;
        const newIndex = isLastSlide ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
    };

    useEffect(() => {
        if (projects.length <= 1) return;
        const sliderTimer = setTimeout(goToNext, 7000);
        return () => clearTimeout(sliderTimer);
    }, [currentIndex, projects.length]);

    if (!projects || projects.length === 0) {
        return (
            <div className="relative h-screen w-full overflow-hidden bg-brand-dark">
                {fallback ? fallback : (
                     <div className="h-full flex items-center justify-center text-center text-white px-4">
                        <div className="container mx-auto">
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight font-heading">Crafting Spaces, Inspiring Life.</h1>
                            <p className="mt-4 text-lg md:text-xl text-gray-300 max-w-2xl mx-auto font-body">Minimalist Designs for a Modern World.</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative h-screen w-full overflow-hidden bg-brand-dark">
            {projects.map((project, index) => (
                <div
                    key={project.id}
                    className={`slide absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                >
                    <div className="absolute inset-0 overflow-hidden">
                        <OptimizedImage
                            src={project.images[0]?.url}
                            size={1920}
                            alt={project.title}
                            className={`w-full h-full object-cover ken-burns-effect ${index === currentIndex ? 'active' : ''}`}
                        />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                    <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white p-4">
                        <div className={`transition-all duration-700 ease-out ${index === currentIndex ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                            <p className="text-sm uppercase tracking-widest font-header">{project.category}</p>
                            <h2 className="text-4xl md:text-6xl font-bold my-4 font-heading">{project.title}</h2>
                            <Link to={`/project/${project.slug}`} className="mt-4 inline-block px-8 py-3 border border-white text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-black transition-colors font-header">
                                View Project
                            </Link>
                        </div>
                    </div>
                </div>
            ))}
            
            {projects.length > 1 && (
                <>
                    <button
                        onClick={goToPrev}
                        className="absolute top-1/2 left-4 md:left-8 -translate-y-1/2 z-20 p-3 bg-white/20 text-white rounded-full hover:bg-white/40 transition-colors"
                        aria-label="Previous slide"
                    >
                        <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    <button
                        onClick={goToNext}
                        className="absolute top-1/2 right-4 md:right-8 -translate-y-1/2 z-20 p-3 bg-white/20 text-white rounded-full hover:bg-white/40 transition-colors"
                        aria-label="Next slide"
                    >
                        <ChevronRightIcon className="w-6 h-6" />
                    </button>
                </>
            )}

            <style>{`
                @keyframes kenburns-top-left {
                    0% { transform: scale(1) translate(0, 0); }
                    100% { transform: scale(1.1) translate(-2%, -2%); }
                }
                .ken-burns-effect.active {
                    animation: kenburns-top-left 8s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default HeroSlider;
